import { useEffect, useState } from "react";
import type { LinkHubLandingConfig } from "@/components/organisms/LinkHub";
import { PLATFORM_TENANT_SLUG } from "@/constants";
import { getLandingProfileMock } from "@/data/tenant/landingProfile";
import { linkhubService, tenantsService } from "@/services";
import {
  buildSocialLinksFromContact,
  pickSpaceContact,
} from "@/utils/socialLinks";
import { useTenant } from "./useTenant";

interface UseLinkHubLandingReturn {
  config: LinkHubLandingConfig | null;
  loading: boolean;
  error: Error | null;
}

interface UseLinkHubLandingOptions {
  slug?: string;
}

/**
 * Loads LinkHub landing config from public tenant profile, profile mock and linkhub API
 */
export const useLinkHubLanding = (
  options: UseLinkHubLandingOptions = {},
): UseLinkHubLandingReturn => {
  const {
    tenantSlug,
    effectiveTenantSlug,
    tenant,
    space,
    loading: tenantLoading,
    error: tenantError,
  } = useTenant();

  const resolvedSlug = options.slug ?? tenantSlug ?? effectiveTenantSlug;
  const [config, setConfig] = useState<LinkHubLandingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!resolvedSlug) {
      setConfig(null);
      setError(new Error("No se pudo resolver el tenant"));
      setLoading(false);
      return;
    }

    if (tenantLoading) {
      setLoading(true);
      return;
    }

    if (tenantError) {
      setConfig(null);
      setError(tenantError);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadLanding = async () => {
      setLoading(true);
      setError(null);

      try {
        const [profile, linkhub] = await Promise.all([
          tenant && tenant.slug === resolvedSlug
            ? Promise.resolve(tenant)
            : tenantsService.getPublicBySlug(resolvedSlug),
          linkhubService.getPublicBySlug(resolvedSlug),
        ]);

        if (cancelled) {
          return;
        }

        const activeSpace =
          tenant && tenant.slug === resolvedSlug ? space : profile.space;

        if (!activeSpace) {
          throw new Error(`Tenant no encontrado: ${resolvedSlug}`);
        }

        const profileMock = getLandingProfileMock(
          resolvedSlug,
          profile.isPlatform,
        );

        setConfig({
          profile: {
            title: activeSpace.name,
            imageUrl:
              profileMock.imageUrl ?? activeSpace.logo ?? activeSpace.picture ?? "",
            imageAlt: profileMock.imageAlt ?? activeSpace.name,
            ...(activeSpace.phrase ? { phrase: activeSpace.phrase } : {}),
          },
          socialLinks: buildSocialLinksFromContact(
            pickSpaceContact(activeSpace.contact),
            activeSpace.name,
          ),
          sections: linkhub.sections,
          analyticsPrefix:
            resolvedSlug === PLATFORM_TENANT_SLUG
              ? profileMock.analyticsPrefix
              : resolvedSlug,
        });
      } catch (err) {
        if (!cancelled) {
          setConfig(null);
          setError(
            err instanceof Error
              ? err
              : new Error("No se pudo cargar la landing"),
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadLanding();

    return () => {
      cancelled = true;
    };
  }, [
    resolvedSlug,
    space,
    tenant,
    tenantError,
    tenantLoading,
  ]);

  return { config, loading, error };
};

/**
 * @deprecated Use useLinkHubLanding instead
 */
export const useTenantLanding = useLinkHubLanding;
